(() => {
  if (customElements.get('ckc-collection-pagination')) return;
  class CKCCollectionPagination extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('#product-grid');
      this.status = this.querySelector('[data-ckc-collection-status]');
      this.button = this.querySelector('[data-ckc-load-more]');
      this.controls = this.querySelector('[data-ckc-pagination-controls]');
      this.native = this.querySelector('[data-ckc-native-pagination]');
      if (!this.grid || !this.button || window.Shopify?.designMode) return;
      this.base = new URL(location.href);
      this.base.searchParams.delete('section_id');
      this.phase = this.dataset.phase;
      this.seen = new Set([...this.grid.children].map(card => card.dataset.ckcProductId).filter(Boolean));
      this.next = this.dataset.next || '';
      this.done = false;
      this.busy = false;
      this.button.addEventListener('click', () => this.load());
      // Native fallback phase links retain price, fabric, style and other filters.
      for (const [selector, phase] of [['[data-ckc-sold-out-link]', '0'], ['[data-ckc-available-link]', '1']]) {
        const link = this.querySelector(selector);
        if (link) link.href = this.phaseURL(phase).href;
      }
      if (this.phase === 'mixed') {
        // Replace the native mixed page only after a complete successful response.
        this.bootstrap();
      } else {
        this.native.hidden = true;
        this.controls.hidden = false;
        this.update();
        if (!this.grid.children.length) this.load();
      }
    }
    disconnectedCallback() { this.controller?.abort(); }
    phaseURL(availability) {
      const url = new URL(this.base);
      url.searchParams.set('filter.v.availability', availability);
      url.searchParams.delete('page');
      if (!url.searchParams.has('sort_by')) url.searchParams.set('sort_by', this.dataset.defaultSort);
      return url;
    }
    async fetchPage(target) {
      const url = new URL(target, this.base);
      if (url.origin !== location.origin || url.pathname !== this.base.pathname) throw new Error('Invalid collection page');
      url.searchParams.set('section_id', this.dataset.section);
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 12000);
      try {
        const response = await fetch(url, {credentials: 'same-origin', signal: this.controller.signal});
        if (!response.ok) throw new Error('Collection unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = doc.querySelector('ckc-collection-pagination');
        if (!section || section.dataset.section !== this.dataset.section) throw new Error('Collection response missing');
        const next = section.dataset.next || '';
        if (next) {
          const nextURL = new URL(next, this.base);
          // Only trust the pagination cursor; retain the exact shopper filter context.
          const preserved = new URL(target, this.base);
          const nextPage = Number(nextURL.searchParams.get('page'));
          const currentPage = Number(preserved.searchParams.get('page') || 1);
          if (!Number.isInteger(nextPage) || nextPage <= currentPage) throw new Error('Invalid collection cursor');
          preserved.searchParams.delete('section_id');
          preserved.searchParams.set('page', nextURL.searchParams.get('page'));
          return {cards: [...section.querySelector('#product-grid').children], next: preserved.href};
        }
        return {cards: [...section.querySelector('#product-grid').children], next: ''};
      } finally { clearTimeout(timeout); }
    }
    async bootstrap() {
      this.busy = true;
      this.grid.setAttribute('aria-busy', 'true');
      try {
        const url = this.phaseURL('1');
        const result = await this.fetchPage(url);
        if (!this.isConnected) return;
        this.grid.replaceChildren();
        this.seen.clear();
        this.phase = 'available';
        this.next = result.next;
        this.append(result.cards);
        this.native.hidden = true;
        this.controls.hidden = false;
        this.status.textContent = '';
      } catch (_) {
        if (this.isConnected) this.status.textContent = this.dataset.textFallback;
        return;
      } finally {
        this.busy = false;
        this.grid.removeAttribute('aria-busy');
      }
      this.update();
      if (!this.grid.children.length) this.load();
    }
    append(cards) {
      let added = 0;
      for (const card of cards) {
        const id = card.dataset.ckcProductId;
        const available = card.dataset.ckcAvailable === 'true';
        if (!id || this.seen.has(id) || (this.phase === 'sold' ? available : !available)) continue;
        this.seen.add(id);
        card.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.fetchPriority = 'auto'; });
        card.classList.add('scroll-trigger--cancel');
        this.grid.append(card);
        added++;
      }
      return added;
    }
    update() {
      if (!this.next && this.phase === 'available') {
        this.phase = 'sold';
        this.next = this.phaseURL('0').href;
      }
      const shownText = this.dataset.textShown?.replace('__COUNT__', String(this.grid.children.length));
      if (shownText) {
        ['ProductCount', 'ProductCountDesktop'].forEach(id => {
          const count = document.getElementById(id);
          if (count) {
            count.textContent = shownText;
            count.title = this.dataset.textOrderingNote || '';
          }
        });
      }
      this.done = !this.next;
      this.button.hidden = this.done;
      this.button.disabled = this.busy;
      this.button.textContent = this.busy ? this.dataset.textLoading : this.dataset.textLoadMore;
      if (this.done) this.status.textContent = this.grid.children.length ? this.dataset.textEnd : this.dataset.textEmpty;
    }
    async load() {
      if (this.busy || this.done || !this.isConnected) return;
      this.busy = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.update();
      let added = 0;
      try {
        // Sparse sold-out variant pages are traversed without downloading the whole catalogue.
        for (let request = 0; request < 3 && this.next && added === 0; request++) {
          const target = this.next;
          const result = await this.fetchPage(target);
          if (!this.isConnected) return;
          this.next = result.next;
          added += this.append(result.cards);
          this.update();
        }
        if (this.isConnected && !this.done) this.status.textContent = added ? this.dataset.textAdded.replace('__COUNT__', String(added)) : this.dataset.textSparse;
      } catch (_) {
        if (this.isConnected) this.status.textContent = this.dataset.textError;
      } finally {
        this.busy = false;
        this.grid.removeAttribute('aria-busy');
        if (this.isConnected) this.update();
      }
    }
  }
  customElements.define('ckc-collection-pagination', CKCCollectionPagination);
})();
